import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { theme, media } from '../styles/theme';
import { RootState } from '../redux/store';
import { removeFromBuylist, updateBuylistQuantity, clearBuylist, setPaymentMethod } from '../redux/buylistSlice';
import { BuylistPaymentMethod } from '../types';
import { getBuylistImageUrl } from '../services/buylistService';
import { beginLogin } from '../services/shopifyAuth';
import BuylistLayout from '../components/buylist/BuylistLayout';
import { FoilBadge } from '../styles/components';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 2rem auto;
  padding: 0 1rem;

  ${media.mobile} {
    padding: 0 0.75rem;
    margin: 1rem auto;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: ${theme.colors.text};

  ${media.mobile} {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${theme.colors.muted};
  font-size: 1.2rem;
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.md};
`;

const CartLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  ${media.mobile} {
    gap: 1rem;
  }
`;

const CartContainer = styled.div`
  margin-bottom: 2rem;
  overflow-x: auto;

  ${media.tablet} {
    overflow-x: visible;
  }
`;

const CartTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.card};
  margin-bottom: 1rem;
  table-layout: fixed;
  min-width: 600px;

  ${media.tablet} {
    display: none;
  }
`;

const TableHead = styled.thead`
  background-color: ${theme.colors.background};

  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    border-bottom: 2px solid ${theme.colors.border};
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${theme.colors.border};

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: ${theme.colors.background};
    }
  }

  td {
    padding: 1rem;
    vertical-align: middle;
  }
`;

const ItemImage = styled.div<{ $imageUrl: string }>`
  width: 60px;
  height: 60px;
  background-image: url(${props => props.$imageUrl});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.sm};
`;

const ItemName = styled.h3`
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
  color: ${theme.colors.text};
`;

const ItemInfo = styled.p`
  font-size: 0.85rem;
  color: ${theme.colors.muted};
  margin: 0 0 0.25rem 0;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.background};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  padding: 0;

  &:hover {
    background-color: ${theme.colors.border};
  }

  &:disabled {
    background-color: ${theme.colors.background};
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const QuantityDisplay = styled.span`
  padding: 0 0.75rem;
  border-top: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.border};
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.danger};
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: ${theme.borderRadius.sm};

  &:hover {
    background-color: rgba(231, 76, 60, 0.1);
  }
`;

const Price = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${theme.colors.success};
`;

const OrderSummary = styled.div`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: 1.5rem;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.card};

  ${media.mobile} {
    padding: 1rem;
  }
`;

const SummaryTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${theme.colors.text};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${theme.colors.border};

  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.1rem;
    padding-top: 1rem;
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 1rem;
  margin-top: 1.5rem;
  background-color: ${theme.colors.success};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${theme.colors.success};
    filter: brightness(0.85);
  }

  &:disabled {
    background-color: ${theme.colors.gray.medium};
    cursor: not-allowed;
    filter: none;
  }
`;

const PaymentMethodContainer = styled.div`
  margin-top: 1.5rem;
`;

const PaymentMethodTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: ${theme.colors.text};
`;

const PaymentMethodOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PaymentMethodOption = styled.label`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: border-color 0.3s, background-color 0.3s;

  &:hover {
    background-color: ${theme.colors.background};
  }

  &.selected {
    border-color: ${theme.colors.primary};
    background-color: rgba(57, 132, 198, 0.1);
  }
`;

const RadioInput = styled.input`
  margin-right: 0.75rem;
`;

// Guest store-credit call-to-action: a locked action that expands an explanation
// inline rather than masquerading as a selectable radio option.
const StoreCreditLockedCard = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.75rem;
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background-color: transparent;
  cursor: pointer;
  transition: border-color 0.3s, background-color 0.3s;

  &:hover {
    background-color: ${theme.colors.background};
    border-color: ${theme.colors.accent};
  }

  &.open {
    border-color: ${theme.colors.accent};
    border-style: solid;
  }
`;

const LockIcon = styled.span`
  font-size: 1.1rem;
  line-height: 1;
`;

const StoreCreditCardText = styled.div`
  flex: 1;
`;

const Chevron = styled.span<{ $open: boolean }>`
  font-size: 0.8rem;
  color: ${theme.colors.muted};
  transition: transform 0.3s;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const StoreCreditInfoPanel = styled.div`
  margin-top: 0.5rem;
  padding: 0.85rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background-color: rgba(45, 184, 107, 0.08);

  p {
    margin: 0 0 0.6rem 0;
    font-size: 0.85rem;
    color: ${theme.colors.text};
    line-height: 1.4;
  }
`;

const StoreCreditLoginButton = styled.button`
  width: 100%;
  padding: 0.7rem;
  background-color: ${theme.colors.success};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.3s;

  &:hover {
    filter: brightness(0.9);
  }
`;

const StoreCreditDismissButton = styled.button`
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.45rem;
  background: none;
  border: none;
  color: ${theme.colors.muted};
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.danger};
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

// Mobile card layout for items (shown on tablet and below)
const MobileCardList = styled.div`
  display: none;

  ${media.tablet} {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const MobileCard = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.card};
`;

const MobileCardImage = styled.div<{ $imageUrl: string }>`
  width: 60px;
  height: 80px;
  flex-shrink: 0;
  background-image: url(${props => props.$imageUrl});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.sm};
`;

const MobileCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
`;

const ITEMS_PER_PAGE = 50;

// Query param that carries a guest's store-credit intent through the Shopify
// login round-trip. Encoded in the return URL (which the OAuth flow already
// preserves) so it reliably reaches the cart and is visible in the address bar.
const PAYOUT_INTENT_PARAM = 'payout';
const STORE_CREDIT_INTENT = 'store_credit';

// Hand off to Shopify login, remembering that the user wants store credit.
const beginStoreCreditLogin = () => {
  beginLogin(`/buylist-cart?${PAYOUT_INTENT_PARAM}=${STORE_CREDIT_INTENT}`);
};

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 0.25rem 0.5rem;
  margin: 0 0.15rem;
  background-color: ${props => props.active ? theme.colors.primary : theme.colors.white};
  color: ${props => props.active ? theme.colors.white : theme.colors.text};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.9rem;
  min-width: 32px;
  text-align: center;

  &:hover {
    background-color: ${props => props.active ? theme.colors.primary : theme.colors.background};
    opacity: ${props => props.active ? 0.9 : 1};
  }

  &:disabled {
    background-color: ${theme.colors.border};
    color: ${theme.colors.gray.medium};
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  text-align: center;
  color: ${theme.colors.muted};
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const BuylistCartPage: React.FC = () => {
  const { items, totalQuantity, totalAmount, paymentMethod } = useSelector((state: RootState) => state.buylist);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Whether the guest store-credit explanation panel is expanded
  const [storeCreditInfoOpen, setStoreCreditInfoOpen] = useState(false);

  // Store credit requires a logged-in Shopify customer; never leave it selected for a guest
  useEffect(() => {
    if (!isAuthenticated && paymentMethod === BuylistPaymentMethod.STORE_CREDIT) {
      dispatch(setPaymentMethod(BuylistPaymentMethod.KONTO));
    }
  }, [isAuthenticated, paymentMethod, dispatch]);

  // Restore the store-credit choice the user made before logging in, now that
  // they're back and authenticated, so they don't have to pick it again. The
  // intent rides the return URL; consume it and clean the param afterwards.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (searchParams.get(PAYOUT_INTENT_PARAM) === STORE_CREDIT_INTENT) {
      dispatch(setPaymentMethod(BuylistPaymentMethod.STORE_CREDIT));
      const next = new URLSearchParams(searchParams);
      next.delete(PAYOUT_INTENT_PARAM);
      setSearchParams(next, { replace: true });
    }
  }, [isAuthenticated, searchParams, setSearchParams, dispatch]);

  const handleRemoveItem = (buylistId: number) => {
    dispatch(removeFromBuylist(buylistId));
  };
  
  const handleQuantityChange = (buylistId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      dispatch(removeFromBuylist(buylistId));
    } else {
      dispatch(updateBuylistQuantity({ buylistId, quantity: newQuantity }));
    }
  };
  
  const handlePaymentMethodChange = (method: BuylistPaymentMethod) => {
    if (method === BuylistPaymentMethod.STORE_CREDIT && !isAuthenticated) {
      beginStoreCreditLogin();
      return;
    }
    dispatch(setPaymentMethod(method));
  };
  
  const handleClearCart = () => {
    dispatch(clearBuylist());
  };
  
  const handleCheckout = () => {navigate('/buylist-checkout');};
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
  const paginatedItems = items.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

  // Reset page when items are removed and current page is out of bounds
  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [items.length, currentPage, totalPages]);

  const getPageNumbers = () => {
    const maxPageButtons = 5;
    const pageNumbers: number[] = [];

    if (totalPages <= maxPageButtons) {
      for (let i = 0; i < totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(0, currentPage - Math.floor(maxPageButtons / 2));
      let endPage = Math.min(totalPages - 1, startPage + maxPageButtons - 1);

      if (endPage - startPage < maxPageButtons - 1) {
        startPage = Math.max(0, endPage - maxPageButtons + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const isCheckoutDisabled = items.length === 0 || !paymentMethod;
  
  return (
    <BuylistLayout>
      <Helmet>
        <title>Salgskurv | Tryllepapp</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageContainer>
        <PageTitle>Salgskurv</PageTitle>
      
      {items.length === 0 ? (
        <EmptyCart>
          <p>Salgskurven din er tom.</p>
          <button
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.borderRadius.sm, cursor: 'pointer' }}
            onClick={() => navigate('/buylist')}
          >
            Gå til kjøpsliste
          </button>
        </EmptyCart>
      ) : (
        <CartLayout>
          <div>
            <h3>Varer i salgskurven</h3>
            <CartContainer>
              <CartTable>
                <TableHead>
                  <tr>
                    <th style={{ width: '70px' }}>Vare</th>
                    <th>Detaljer</th>
                    <th style={{ width: '120px' }}>Pris</th>
                    <th style={{ width: '110px' }}>Antall</th>
                    <th style={{ width: '90px' }}>Totalt</th>
                    <th style={{ width: '105px' }}>Handlinger</th>
                  </tr>
                </TableHead>
                <TableBody>
                  {paginatedItems.map(({ item, quantity }) => (
                    <tr key={item.buylistID}>
                      <td>
                        <ItemImage $imageUrl={getBuylistImageUrl(item.cardBlueprintId, item.image_url)} />
                      </td>
                      <td>
                        <ItemName>{item.blueprintName}</ItemName>
                        <ItemInfo>{item.expansionName}</ItemInfo>
                        <ItemInfo>{item.blueprintVersion}</ItemInfo>
                        {item.foil && <FoilBadge>✨ Foil</FoilBadge>}
                        <ItemInfo>Maks antall: {item.maxQuantity}</ItemInfo>
                      </td>
                      <td>
                        <ItemInfo>{(item.buyPrice || 0).toFixed(2)} kr per stk</ItemInfo>
                        {item.buylistStoreCreditPrice && (
                          <ItemInfo style={{ color: theme.colors.primary }}>
                            {item.buylistStoreCreditPrice.toFixed(2)} kr butikkreditt
                          </ItemInfo>
                        )}
                      </td>
                      <td>
                        <QuantityControl>
                          <QuantityButton
                            onClick={() => handleQuantityChange(item.buylistID, quantity - 1)}
                            title="Reduser antall"
                          >
                            -
                          </QuantityButton>
                          <QuantityDisplay>{quantity}</QuantityDisplay>
                          <QuantityButton 
                            onClick={() => handleQuantityChange(item.buylistID, quantity + 1)}
                            disabled={quantity >= item.maxQuantity}
                            title="Øk antall"
                          >
                            +
                          </QuantityButton>
                        </QuantityControl>
                      </td>
                      <td>
                        <Price>{((item.buyPrice || 0) * quantity).toFixed(2)} kr</Price>
                      </td>
                      <td>
                        <ActionButton 
                          onClick={() => handleRemoveItem(item.buylistID)}
                          title="Fjern vare"
                        >
                          ×
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </TableBody>
              </CartTable>

              <MobileCardList>
                {paginatedItems.map(({ item, quantity }) => (
                  <MobileCard key={item.buylistID}>
                    <MobileCardImage $imageUrl={getBuylistImageUrl(item.cardBlueprintId, item.image_url)} />
                    <MobileCardContent>
                      <ItemName>{item.blueprintName}</ItemName>
                      <ItemInfo>{item.expansionName}</ItemInfo>
                      {item.foil && <FoilBadge>✨ Foil</FoilBadge>}
                      <ItemInfo>{(item.buyPrice || 0).toFixed(2)} kr per stk</ItemInfo>
                      <MobileCardRow>
                        <QuantityControl>
                          <QuantityButton
                            onClick={() => handleQuantityChange(item.buylistID, quantity - 1)}
                            title="Reduser antall"
                          >
                            -
                          </QuantityButton>
                          <QuantityDisplay>{quantity}</QuantityDisplay>
                          <QuantityButton
                            onClick={() => handleQuantityChange(item.buylistID, quantity + 1)}
                            disabled={quantity >= item.maxQuantity}
                            title="Øk antall"
                          >
                            +
                          </QuantityButton>
                        </QuantityControl>
                        <Price>{((item.buyPrice || 0) * quantity).toFixed(2)} kr</Price>
                        <ActionButton
                          onClick={() => handleRemoveItem(item.buylistID)}
                          title="Fjern vare"
                        >
                          ×
                        </ActionButton>
                      </MobileCardRow>
                    </MobileCardContent>
                  </MobileCard>
                ))}
              </MobileCardList>

              {totalPages > 1 && (
                <>
                  <Pagination>
                    <PageButton
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage(0)}
                    >
                      «
                    </PageButton>
                    <PageButton
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      ‹
                    </PageButton>

                    {getPageNumbers().map(pageNum => (
                      <PageButton
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum + 1}
                      </PageButton>
                    ))}

                    <PageButton
                      disabled={currentPage === totalPages - 1}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      ›
                    </PageButton>
                    <PageButton
                      disabled={currentPage === totalPages - 1}
                      onClick={() => setCurrentPage(totalPages - 1)}
                    >
                      »
                    </PageButton>
                  </Pagination>

                  <PageInfo>
                    Viser {currentPage * ITEMS_PER_PAGE + 1} - {Math.min((currentPage + 1) * ITEMS_PER_PAGE, items.length)} av {items.length} varer
                  </PageInfo>
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ClearButton onClick={handleClearCart}>Tøm salgskurv</ClearButton>
              </div>
            </CartContainer>
          </div>
          
          <OrderSummary>
            <SummaryTitle>Ordresammendrag</SummaryTitle>
            <SummaryRow>
              <span>Totalt antall:</span>
              <span>{totalQuantity}</span>
            </SummaryRow>
            <SummaryRow>
              <span>Totalt kontantbeløp:</span>
              <span>{totalAmount.toFixed(2)} kr</span>
            </SummaryRow>
            <SummaryRow style={{ backgroundColor: 'rgba(45, 184, 107, 0.1)', padding: '10px', borderRadius: theme.borderRadius.sm }}>
              <span style={{ fontWeight: 'bold', color: theme.colors.success }}>Butikkreditt-verdi (10% bonus):</span>
              <span style={{ fontWeight: 'bold', color: theme.colors.success }}>{(totalAmount * 1.1).toFixed(2)} kr</span>
            </SummaryRow>
            
            <PaymentMethodContainer>
              <PaymentMethodTitle>Velg utbetalingsmetode</PaymentMethodTitle>
              <PaymentMethodOptions>
                <PaymentMethodOption className={paymentMethod === BuylistPaymentMethod.KONTO ? 'selected' : ''}>
                  <RadioInput 
                    type="radio" 
                    name="paymentMethod" 
                    checked={paymentMethod === BuylistPaymentMethod.KONTO}
                    onChange={() => handlePaymentMethodChange(BuylistPaymentMethod.KONTO)}
                  />
                  Bankoverføring
                </PaymentMethodOption>
                {isAuthenticated ? (
                  <PaymentMethodOption className={paymentMethod === BuylistPaymentMethod.STORE_CREDIT ? 'selected' : ''}>
                    <RadioInput
                      type="radio"
                      name="paymentMethod"
                      checked={paymentMethod === BuylistPaymentMethod.STORE_CREDIT}
                      onChange={() => handlePaymentMethodChange(BuylistPaymentMethod.STORE_CREDIT)}
                    />
                    <div>
                      <div>Butikkreditt (+10% verdi)</div>
                      <div style={{ fontSize: '0.8rem', color: theme.colors.success, marginTop: '2px' }}>
                        Få {(totalAmount * 1.1).toFixed(2)} kr i stedet for {totalAmount.toFixed(2)} kr!
                      </div>
                    </div>
                  </PaymentMethodOption>
                ) : (
                  <div>
                    <StoreCreditLockedCard
                      type="button"
                      className={storeCreditInfoOpen ? 'open' : ''}
                      onClick={() => setStoreCreditInfoOpen((open) => !open)}
                      aria-expanded={storeCreditInfoOpen}
                    >
                      <LockIcon aria-hidden="true">🔒</LockIcon>
                      <StoreCreditCardText>
                        <div>Butikkreditt (+10% verdi)</div>
                        <div style={{ fontSize: '0.8rem', color: theme.colors.success, marginTop: '2px' }}>
                          Få {(totalAmount * 1.1).toFixed(2)} kr i stedet for {totalAmount.toFixed(2)} kr!
                        </div>
                      </StoreCreditCardText>
                      <Chevron $open={storeCreditInfoOpen} aria-hidden="true">▼</Chevron>
                    </StoreCreditLockedCard>

                    {storeCreditInfoOpen && (
                      <StoreCreditInfoPanel>
                        <p>
                          Butikkreditt utbetales til Shopify-kontoen din. Logg inn eller opprett en
                          konto, så krediterer vi deg automatisk når ordren er ferdig gradert.
                        </p>
                        <p style={{ color: theme.colors.muted }}>
                          Salgskurven din beholdes – du kommer rett tilbake hit etter innlogging.
                        </p>
                        <StoreCreditLoginButton
                          type="button"
                          onClick={beginStoreCreditLogin}
                        >
                          Logg inn / opprett konto
                        </StoreCreditLoginButton>
                        <StoreCreditDismissButton
                          type="button"
                          onClick={() => setStoreCreditInfoOpen(false)}
                        >
                          Avbryt
                        </StoreCreditDismissButton>
                      </StoreCreditInfoPanel>
                    )}
                  </div>
                )}
              </PaymentMethodOptions>
            </PaymentMethodContainer>
            
            <CheckoutButton
              onClick={handleCheckout}
              disabled={isCheckoutDisabled}
            >
              Gå til kassen
            </CheckoutButton>
          </OrderSummary>
        </CartLayout>
      )}
      </PageContainer>
    </BuylistLayout>
  );
};

export default BuylistCartPage;
